import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { KeyRound, Plus, ChevronDown, ChevronRight } from "lucide-react";
import { DetailHeader, BackLink } from "@/components/DetailHeader";
import { PropGrid } from "@/components/PropGrid";
import { SectionCard } from "@/components/SectionCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { OneTimeTokenDialog } from "@/components/OneTimeTokenDialog";
import {
  ActivationKeyStatusBadge,
  DeploymentStatusBadge,
  LicenseStatusBadge,
  TrialBadge,
} from "@/components/StatusBadges";
import { GenerateActivationKeyDialog } from "@/components/dialogs/GenerateActivationKeyDialog";
import { ExtendLicenseDialog } from "@/components/dialogs/ExtendLicenseDialog";
import { useLicense } from "@/api/queries/licenses";
import { useCustomer } from "@/api/queries/customers";
import { useActivationKeys } from "@/api/queries/activationKeys";
import { useDeployments, useDeploymentUsage } from "@/api/queries/deployments";
import { useRevokeLicense, useSuspendLicense } from "@/api/mutations/licenses";
import { useDeactivateDeployment } from "@/api/mutations/deployments";
import { relativeTime } from "@/lib/relativeTime";
import type { Deployment } from "@/types";

// Aufklappbare Deployment-Zeile + Usage-Zeitreihe (lazy geladen bei Expand).
function DeploymentRow({ dep, licenseId }: { dep: Deployment; licenseId: string }) {
  const [expanded, setExpanded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const usage = useDeploymentUsage(expanded ? dep.id : undefined, 50);
  const deactivate = useDeactivateDeployment();

  return (
    <>
      <TableRow data-clickable="true" onClick={() => setExpanded((v) => !v)}>
        <TableCell>
          <span className="flex items-center gap-1.5">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span className="mono text-xs">{dep.id}</span>
          </span>
        </TableCell>
        <TableCell>
          <DeploymentStatusBadge status={dep.status} />
        </TableCell>
        <TableCell className="tnum">{dep.leaseSeq}</TableCell>
        <TableCell className="tnum">{dep.refreshCounter}</TableCell>
        <TableCell className="text-text3">{relativeTime(dep.lastRefreshAt)}</TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          {dep.status === "active" && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              data-testid={`deactivate-${dep.id}`}
            >
              Deaktivieren
            </Button>
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="bg-surface2/40">
            {usage.isLoading ? (
              <div className="p-2 text-sm text-text3">Lade Usage…</div>
            ) : (usage.data ?? []).length === 0 ? (
              <div className="p-2 text-sm text-text3">Keine Usage-Reports.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Zeit</TableHead>
                    <TableHead>Agents</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>Nodes</TableHead>
                    <TableHead>Channels</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(usage.data ?? []).map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-text3">{relativeTime(r.serverTime)}</TableCell>
                      <TableCell className="tnum">{r.agents}</TableCell>
                      <TableCell className="tnum">{r.users}</TableCell>
                      <TableCell className="tnum">{r.nodes}</TableCell>
                      <TableCell className="tnum">{r.channels}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableCell>
        </TableRow>
      )}
      {confirmOpen && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title="Deployment deaktivieren"
          description="Refresh wird danach verweigert → der Master degradiert sanft (GRACE→DEGRADED, kein harter Lock). Wiederherstellung nur ueber einen neuen Aktivierungsschluessel."
          confirmText="Deaktivieren"
          confirmVariant="destructive"
          isPending={deactivate.isPending}
          errorMessage={deactivate.isError ? deactivate.error.message : undefined}
          onConfirm={() =>
            deactivate.mutate(
              { id: dep.id, licenseId },
              { onSuccess: () => setConfirmOpen(false) },
            )
          }
        />
      )}
    </>
  );
}

export function LicenseDetail() {
  const { id } = useParams<{ id: string }>();
  const license = useLicense(id);
  const customer = useCustomer(license.data?.customerId);
  const keys = useActivationKeys(id);
  const deployments = useDeployments(id);

  const revoke = useRevokeLicense();
  const suspend = useSuspendLicense();

  const [revokeOpen, setRevokeOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [plaintextKey, setPlaintextKey] = useState<string | null>(null);

  if (license.isLoading) {
    return (
      <div className="max-w-6xl">
        <Card>
          <CardContent className="p-6" data-testid="detail-loading">
            Lade…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (license.isError || !license.data) {
    return (
      <div className="max-w-6xl space-y-4">
        <BackLink to="/licenses" label="Lizenzen" />
        <Card>
          <CardContent className="p-6 text-danger" data-testid="detail-error">
            Lizenz nicht gefunden.
          </CardContent>
        </Card>
      </div>
    );
  }

  const l = license.data;

  return (
    <div className="max-w-6xl space-y-5">
      <DetailHeader
        backTo="/licenses"
        backLabel="Lizenzen"
        icon={KeyRound}
        title={l.edition}
        badge={
          <span className="flex items-center gap-2">
            <LicenseStatusBadge status={l.status} />
            {l.trial && <TrialBadge />}
          </span>
        }
        sub={
          customer.data ? (
            <Link to={`/customers/${l.customerId}`} className="text-accent hover:underline">
              {customer.data.name}
            </Link>
          ) : (
            <span className="mono text-xs">{l.customerId}</span>
          )
        }
        actions={
          <>
            <Button variant="outline" onClick={() => setExtendOpen(true)} disabled={l.status === "revoked"} data-testid="extend-button">
              Verlaengern
            </Button>
            <Button
              variant={l.status === "suspended" ? "default" : "outline"}
              disabled={l.status === "revoked" || suspend.isPending}
              onClick={() => suspend.mutate({ id: l.id, suspended: l.status !== "suspended" })}
              data-testid="toggle-suspend-license"
            >
              {l.status === "suspended" ? "Entsperren" : "Sperren"}
            </Button>
            <Button variant="destructive" onClick={() => setRevokeOpen(true)} disabled={l.status === "revoked"} data-testid="revoke-button">
              Widerrufen
            </Button>
          </>
        }
      />

      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          {/* Aktivierungsschluessel */}
          <SectionCard
            title="Aktivierungsschluessel"
            description="Klartext erscheint nur einmal bei der Generierung"
            noPad
            action={
              <Button size="sm" onClick={() => setGenOpen(true)} data-testid="generate-key-button">
                <Plus className="h-4 w-4" /> Generieren
              </Button>
            }
          >
            {(keys.data ?? []).length === 0 ? (
              <div className="p-6 text-center text-sm text-text3" data-testid="keys-empty">
                Keine Schluessel.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Angelegt</TableHead>
                    <TableHead>Laeuft ab</TableHead>
                    <TableHead>Verbraucht</TableHead>
                    <TableHead>Deployment</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(keys.data ?? []).map((k) => (
                    <TableRow key={k.id}>
                      <TableCell>
                        <ActivationKeyStatusBadge status={k.status} />
                      </TableCell>
                      <TableCell className="text-text3">{relativeTime(k.createdAt)}</TableCell>
                      <TableCell className="text-text3">{relativeTime(k.expiresAt)}</TableCell>
                      <TableCell className="text-text3">{k.consumedAt ? relativeTime(k.consumedAt) : "—"}</TableCell>
                      <TableCell className="mono text-xs">{k.boundDeploymentId ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>

          {/* Deployments */}
          <SectionCard title="Deployments" description="Aktivierte Installationen dieser Lizenz" noPad>
            {(deployments.data ?? []).length === 0 ? (
              <div className="p-6 text-center text-sm text-text3" data-testid="deployments-empty">
                Keine Deployments.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Deployment</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>leaseSeq</TableHead>
                    <TableHead>Refreshes</TableHead>
                    <TableHead>Letzter Refresh</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(deployments.data ?? []).map((dep) => (
                    <DeploymentRow key={dep.id} dep={dep} licenseId={l.id} />
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        </div>

        <PropGrid
          rows={[
            ["Lizenz-ID", <span className="mono text-xs">{l.id}</span>],
            ["Status", <LicenseStatusBadge status={l.status} />],
            ["Edition", l.edition],
            ["Trial", l.trial ? "ja" : "nein"],
            ["Features", l.features.length ? l.features.join(", ") : "—"],
            ["Limits", `A ${l.limits.agents} · U ${l.limits.users} · N ${l.limits.nodes} · C ${l.limits.channels}`],
            ["Enforcement", `Grace ${l.enforcement.graceDays}d · maxJwtTtl ${l.enforcement.maxJwtTtlSec}s`],
            ["min. Client", l.minClientVersion ?? "—"],
            ["Gueltig ab", l.validFrom.slice(0, 10)],
            ["Gueltig bis", l.validUntil.slice(0, 10)],
            ["Angelegt", relativeTime(l.createdAt)],
          ]}
        />
      </div>

      {revokeOpen && (
        <ConfirmDialog
          open={revokeOpen}
          onOpenChange={setRevokeOpen}
          title="Lizenz widerrufen"
          description="Irreversibel — der Master geht beim naechsten Refresh in den Zustand LOCKED. Zum Bestaetigen die Lizenz-ID eintippen."
          confirmText="Widerrufen"
          confirmVariant="destructive"
          requiredTypeText={l.id}
          isPending={revoke.isPending}
          errorMessage={revoke.isError ? revoke.error.message : undefined}
          onConfirm={() => revoke.mutate(l.id, { onSuccess: () => setRevokeOpen(false) })}
        />
      )}

      {genOpen && (
        <GenerateActivationKeyDialog
          open={genOpen}
          onOpenChange={setGenOpen}
          licenseId={l.id}
          onGenerated={(pt) => setPlaintextKey(pt)}
        />
      )}

      {plaintextKey && (
        <OneTimeTokenDialog
          open={!!plaintextKey}
          onOpenChange={(o) => !o && setPlaintextKey(null)}
          title="Aktivierungsschluessel"
          description="Diesen Schluessel bekommt der Kunde fuer die Erstaktivierung. Nur jetzt sichtbar."
          label="Aktivierungsschluessel"
          token={plaintextKey}
        />
      )}

      {extendOpen && <ExtendLicenseDialog open={extendOpen} onOpenChange={setExtendOpen} license={l} />}
    </div>
  );
}
